import { emptyFragment, useMediaQuery } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import { Experience } from '../../../Common/Experience/Types';
import licensePurchaseStore from '../../../Common/LicensePurchase/Store';
import UnlicensedOrGetAccess from '../../../Common/LicensePurchase/Views/UnlicensedOrGetAccess';
import { useExperienceEnrollmentContext } from '../../../Common/ProgramExperienceEnrollment';
import EnrollableAction from './EnrollableAction';
import { getButtonSize } from './Types';
import NotEnrollableAction from './NotEnrollableAction';
import UpcomingAction from './UpcomingAction';

interface Props {
  experience: Experience;
}

function ExperienceActions({ experience }: Props) {
  const { kind, availabilities, licensingStatus } = experience;
  const { onOpen } = useExperienceEnrollmentContext();
  const breakPoint = useMediaQuery();
  // Get access stands where Get Started does for a licensed participant, so it
  // takes that button's size and width (OnDemandAction).
  const getAccessButton = {
    size: getButtonSize(breakPoint),
    fullWidth: false,
    className: 'min-w-full sm:min-w-48',
  };
  // Preview Only keeps the spacing it always had; only a buyable course's Get
  // access moves to Get Started's spacing.
  const unlicensedSpacing = licensePurchaseStore.canBuy(experience.id)
    ? 'mt-20 sm:mt-10'
    : 'mt-14 lg:mt-6';

  switch (kind) {
    case 'commerce-enrollable':
    case 'enrollable':
      // Check licensing status - unlicensed users can preview but not enroll
      if (licensingStatus === 'unlicensed') {
        return (
          <div className={unlicensedSpacing}>
            <UnlicensedOrGetAccess
              programFamilyId={experience.id}
              courseTitle={experience.title.text}
              experienceId={experience.experienceId}
              buttonProps={getAccessButton}
            />
          </div>
        );
      }
      return (
        <EnrollableAction
          availabilities={availabilities}
          experience={experience}
          onClick={onOpen}
        />
      );
    case 'not-enrollable':
      return <NotEnrollableAction className="mt-14 lg:mt-6" availabilities={availabilities} />;
    case 'not-licensed':
      return (
        <div className={unlicensedSpacing}>
          <UnlicensedOrGetAccess
            programFamilyId={experience.id}
            courseTitle={experience.title.text}
            experienceId={experience.experienceId}
            buttonProps={getAccessButton}
          />
        </div>
      );
    case 'beginnable':
    case 'returnable':
    case 'resumable':
    case 'graduated':
      return emptyFragment();
    case 'upcoming':
      return <UpcomingAction experience={experience} />;
  }
}

export default observer(ExperienceActions);
